
import LevelSelector from '@guzzj/clappr-level-selector-plugin';
import AirPlayPlugin from 'clappr-airplay-plugin';
import ClapprPip from 'clappr-pip';
import QRCode from 'qrcode';
import { Events } from '../events/events.js';
import { customPlayback } from './clappr-class.js';
import { Nodes } from './nodes.js';

// Cargamos Clappr de forma dinámica
const loadClappr = () => import('@clappr/player');

export class Player {

  self = null;
  contentId = null;
  elementSelector = null;
  options = null;
  player = null;
  secondsAlive = 0;
  currentContent = null;
  lastJumpForward = null;
  lastJumpForwardTime = null;
  lastJumpBackward = null;
  lastJumpBackwardTime = null;
  created = 0;

  constructor(_this, contentId, elementSelector, options) {
    this.self = _this;
    this.contentId = parseInt(contentId);
    this.elementSelector = elementSelector;
    this.options = options;
    this.playerElement = null;
    this.playerContainer = null;
    this.playerEvents = {};
    this.loaded = false;

    // Añadimos el player a la lista de players
    this.self.players.push(this);

    this.init();
  }

  // Método para registrar un evento y su manejador
  on(event, listener) {
    if (!this.playerEvents[event]) {
      this.playerEvents[event] = [];
    }
    this.playerEvents[event].push(listener);
  }

  // Método para emitir un evento y ejecutar todos los manejadores registrados
  emit(event, ...args) {
    if (this.playerEvents[event]) {
      this.playerEvents[event].forEach(listener => listener(...args));
    }
  }

  // Método para eliminar un manejador específico de un evento
  off(event, listenerToRemove) {
    if (!this.playerEvents[event]) return;

    this.playerEvents[event] = this.playerEvents[event].filter(listener => listener !== listenerToRemove);
  }

  async init() {

    let url = this.options.lineal ? `${this.contentId}/hls/live.m3u8` : `${this.contentId}/hls/stream.m3u8`;
    let _this = this;

    this.currentContent = await this.self.content.getContent(this.contentId);

    // Cargamos Clappr
    const Clappr = await loadClappr();

    // Si no somos usuario 1, quitamos el log de Clappr
    if (this.self.user.current_user.id != 1) {
      Clappr.Log.setLevel(3);
    }

    var plugins = {
      core: [ClapprPip.PipButton, ClapprPip.PipPlugin, LevelSelector, AirPlayPlugin]
    };

    // Si nos pide chromecast
    if (this.options.chromecast) {
      plugins.playback = [customPlayback];
    }

    // Iniciamos Clappr
    this.player = new Clappr.Player({
      source: url,
      parentId: this.elementSelector,
      autoPlay: this.options.autoPlay || true,
      preload: 'metadata',
      mute: this.options.muted || false,
      playbacks: [customPlayback],
      disableKeyboardShortcuts: true,
      hideMediaControl: this.options.chromecast == false,
      width: "100%",
      height: "100%",
      includeResetStyle: false,
      plugins: plugins,
      poster: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
      levelSelectorConfig: {
        title: 'Calidad',
        labelCallback: function (level, customLabel) {
          // Si es 8k o 4k, devolvemos en texto
          if (level.level.height > 2160) {
            return '8k';
          } else if (level.level.height > 1080) {
            return '4k';
          } else if (level.level.height > 720) {
            return '1080p';
          } else if (level.level.height > 480) {
            return '720p';
          } else if (level.level.height > 360) {
            return '480p';
          } else {
            return level.level.height + 'p';
          }

        }
      },
      playback: {
        playInline: true, // Para que no se abra en pantalla completa en iOS        
        hlsjsConfig: {
          debug: false,
          maxLiveSyncPlaybackRate: 1.1,
          capLevelToPlayerSize: true,  // Limitar calidad al tamaño del reproductor para evitar resoluciones innecesarias
          capLevelOnFPSDrop: true,  // Disminuir calidad si cae el FPS
          liveSyncDuration: 10,  // Ajustar para evitar saltos en directos
          startLevel: 2,  // Comenzar con un nivel de calidad media
          maxBufferLength: 15,  // Mantener un buffer máximo de 15 segundos
          maxBufferSize: 60 * 1000 * 1000,  // Limitar el buffer a 60MB
          backBufferLength: 8,  // Retener sólo 8 segundos de buffer tras la reproducción
          maxMaxBufferLength: 15,  // Limitar el buffer extendido a 15 segundos
          lowLatencyMode: false,  // Desactivar el modo de baja latencia para reducir carga en CPU
          maxFragLookUpTolerance: 0.1,  // Evitar búsqueda innecesaria de fragmentos
          autoLevelCapping: -1,  // Permitir selección de nivel dinámico
          abrBandWidthFactor: 0.7,  // Ajustar para permitir cambios de calidad más suaves
          abrBandWidthUpFactor: 0.5,  // Acelerar subida de nivel de calidad
          abrEwmaFastLive: 2,  // Cálculo de bitrate reactivo
          abrEwmaSlowLive: 5,  // Suavizar cambios de bitrate en streams largos
          manifestLoadingMaxRetry: 10,
          fragLoadingMaxRetry: 10,
          fragLoadingRetryDelay: 100,
          fragLoadingMaxRetryTimeout: 2000,
          levelLoadingMaxRetry: 10,
          manifestLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 15000,
              maxLoadTimeMs: 3000,
              timeoutRetry: {
                maxNumRetry: 5,
                retryDelayMs: 2000,
                maxRetryDelayMs: 4000,
              },
              errorRetry: {
                maxNumRetry: 3,
                retryDelayMs: 1000,
                maxRetryDelayMs: 4000,
                shouldRetry: (retryConfig, retryCount) => retryCount < retryConfig.maxNumRetry
              }
            }
          },
          fragLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 10000,
              maxLoadTimeMs: 2000,
              timeoutRetry: {
                maxNumRetry: 4,
                retryDelayMs: 1000,
                maxRetryDelayMs: 2000,
              },
              errorRetry: {
                maxNumRetry: 3,
                retryDelayMs: 1000,
                maxRetryDelayMs: 2000,
                shouldRetry: (retryConfig, retryCount) => retryCount < retryConfig.maxNumRetry
              }
            }
          },
          playlistLoadPolicy: {
            default: {
              maxTimeToFirstByteMs: 12000,
              maxLoadTimeMs: 2500,
              timeoutRetry: {
                maxNumRetry: 3,
                retryDelayMs: 1500,
                maxRetryDelayMs: 3000,
              },
              errorRetry: {
                maxNumRetry: 3,
                retryDelayMs: 1000,
                maxRetryDelayMs: 3000,
                shouldRetry: (retryConfig, retryCount) => retryCount < retryConfig.maxNumRetry
              }
            }
          },
          xhrSetup: async function (xhr, url) {
            var newurl = await Nodes.prepareUrl(url, _this.contentId);
            url = newurl.url;
            xhr.open('GET', url, true);
            return xhr;
          }
        }
      },
      events: {
        onReady: function () {
          var plugin = this.getPlugin('click_to_pause');
          plugin && plugin.disable();

          // Disparamos el evento
          _this.emit(Events.PLAYER_READY, _this);
        },
        onError: function (e) {
          // Disparamos el evento
          _this.emit(Events.PLAYER_ERROR, e);
        }
      },
    });

    var hls = this.player.core.containers[0].playback._hls;

    // Si no hay hls, es que es chromecast
    hls && hls.on(Clappr.HLS.HLSJS.Events.FRAG_LOADED, function (event, data) {
      // Indicamos al gestor de nodos que se ha cargado un fragmento
      Nodes.fragmentOk(data.frag.url, _this.contentId);
    });

    hls && hls.on(Clappr.HLS.HLSJS.Events.ERROR, function (event, data) {
      if (data.details == 'fragLoadError') {
        // Indicamos al gestor de nodos el error
        Nodes.fragmentError(data.frag.url, _this.contentId);
      }

      if (data.details == 'levelLoadError') {
        // Indicamos al gestor de nodos el error
        Nodes.fragmentError(data.url, _this.contentId);
      }
    });

    // Si nos pide chromecast, le enviamos la info necesaria
    if (this.options.chromecast) {
      this.player.getPlugin('chromecast_playback').loadCastInfo(this, this.currentContent);
    }

    // Metemos un listener de PLAYBACK_LEVELS_AVAILABLE
    var alreadyLevels = false;
    this.player.core.containers[0].playback.on(Clappr.Events.PLAYBACK_LEVELS_AVAILABLE, () => {

      // Si ya hemos pasado por aquí, no hacemos nada
      if (alreadyLevels) {
        return;
      }

      this.loaded = true;

      alreadyLevels = true;

      // Quitamos resoluciones mayores a maxResolution y menores a minResolution
      if (this.options.maxResolution || this.options.minResolution) {

        var playback = this.player.core.getCurrentPlayback();
        var hls = playback._hls;

        // Quitamos las mayores a maxResolution
        if (this.options.maxResolution) {
          for (let i = hls.levels.length - 1; i >= 0; i--) {
            if (hls.levels[i].height > this.options.maxResolution) {

              // Si es la actual, la bajamos
              if (hls.levels[i].height == hls.currentLevel) {
                hls.currentLevel(i - 1);
              }

              // La quitamos del hljs
              hls.removeLevel(i);
            }
          }
        }

        // Quitamos las menores a minResolution
        if (this.options.minResolution) {
          for (let i = 0; i < hls.levels.length; i++) {
            if (hls.levels[i].height >= this.options.minResolution) {
              hls.autoLevelCapping = i;
              break;
            }
          }
        }

        // Le enviamos el evento de PLAYBACK_LEVELS_AVAILABLE para que la quite de la interfaz
        var clapprLevels = [];
        for (let i = 0; i < hls.levels.length; i++) {
          clapprLevels.push({
            id: i,
            label: hls.levels[i].height + 'p',
            level: hls.levels[i]
          });
        }

        this.player.core.containers[0].playback.trigger(Clappr.Events.PLAYBACK_LEVELS_AVAILABLE, clapprLevels);
      }

      // Si nos llega seek, lo hacemos
      if (this.options.seek) {
        this.player.seek(this.options.seek);
      }

      // Disparamos el evento
      _this.emit(Events.PLAYBACK_LEVELS_AVAILABLE, this.contentId);
    });

    // Metemos un listener de MEDIACONTROL_SHOW
    this.player.core.containers[0].on(Clappr.Events.CONTAINER_MEDIACONTROL_SHOW, () => {
      // Disparamos el evento
      _this.emit(Events.PLAYER_CONTROLS_SHOW, this.contentId);
    });

    // Metemos un listener de MEDIACONTROL_HIDE
    this.player.core.containers[0].on(Clappr.Events.CONTAINER_MEDIACONTROL_HIDE, () => {
      // Disparamos el evento
      _this.emit(Events.PLAYER_CONTROLS_HIDE, this.contentId);
    });

    // Metemos un listener de se ha finalizado la reproducción
    this.player.core.containers[0].on(Clappr.Events.CONTAINER_ENDED, () => {
      // Disparamos el evento
      _this.emit(Events.PLAYER_ENDED, this.contentId);
    });

    // Metemos un listener de posición para ir guardando el tiempo
    this.player.core.containers[0].on(Clappr.Events.CONTAINER_TIMEUPDATE, () => {
      if (this.currentContent) {
        this.currentContent.currentTime = this.player.getCurrentTime();
      }
    });

    // Obtenemos el elemento del player
    this.playerContainer = document.querySelector(this.elementSelector);
    this.playerElement = this.playerContainer.querySelector('[data-player]');

    // Ajustamos el tamaño del player
    this.playerElement.style.width = '100%';
    this.playerElement.style.height = '100%';

    // Capturamos el elemento video y le ponemos un poster en blanco
    var videoel = this.playerContainer.querySelector('video');
    videoel && videoel.setAttribute('poster', 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7');

    // Iniciamos el QR
    this.startQR();

    // Iniciamos el Timer de seguimiento de reproducción
    this.startTimer();

    // Si hay autoplay, le volvemos a dar a Play porque a veces no se reproduce
    if (this.options.autoPlay == undefined || this.options.autoPlay == true) {
      this.player.play();
    }

    // Añadimos la acción
    this.self.actions.addAction(TBFront.Actions.CONTENT_PLAY, { content: this.contentId });

    // Enviamos el evento al servidor
    var request = await this.self.api.call('content_play', { cid: this.contentId });

    // Guardamos el play_token e ip
    this.created = request.token;
    this.created_ip = request.play_ip;

    // Actualizamos las reproducciones
    this.self.actions.updatePlaying();
  }

  destroy() {

    // Limpiamos el timer
    clearInterval(this.timer);

    // Mandamos Stop
    this.player.stop();

    // Actualizamos las reproducciones
    this.self.actions.updatePlaying();

    // Lo quitamos de la lista de players
    this.self.players = this.self.players.filter(player => player.contentId != this.contentId);

    // Destruimos el player
    this.player.destroy();
  }

  play() {
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  seek(seconds) {
    this.player.seek(seconds);
  }

  jumpForward() {
    // Miramos si ha pasado más de 1 segundo desde el último salto
    if (this.lastJumpForward && (Date.now() - this.lastJumpForward) < 1000) {
      // Sumamos 10 segundos al tiempo actual
      this.lastJumpForwardTime += 10;
    } else {
      this.lastJumpForwardTime = 5;
    }

    this.lastJumpForward = Date.now();
    this.player.seek(this.player.getCurrentTime() + this.lastJumpForwardTime);

    // Mostramos la interfaz de Clappr
    this.player.core.mediaControl.show();

  }

  jumpBackward() {
    // Miramos si ha pasado más de 1 segundo desde el último salto
    if (this.lastJumpBackward && (Date.now() - this.lastJumpBackward) < 1000) {
      // Restamos 10 segundos al tiempo actual
      this.lastJumpBackwardTime += 10;
    } else {
      this.lastJumpBackwardTime = 5;
    }

    this.lastJumpBackward = Date.now();
    this.player.seek(this.player.getCurrentTime() - this.lastJumpBackwardTime);

    // Mostramos la interfaz de Clappr
    this.player.core.mediaControl.show();
  }

  setVolume(volume) {
    this.player.setVolume(volume);
  }

  getVolume() {
    return this.player.getVolume();
  }

  isPlaying() {
    return this.player.isPlaying();
  }

  isPaused() {
    return this.player.isPaused();
  }

  isEnded() {
    return this.player.isEnded();
  }

  getCurrentTime() {
    return this.player.getCurrentTime();
  }

  getDuration() {
    return this.player.getDuration();
  }

  getPlaybackType() {
    return this.player.getPlaybackType();
  }

  mute() {
    this.player.mute();
  }

  unmute() {
    this.player.unmute();
  }

  fullscreen() {
    this.player.core.mediaControl.toggleFullscreen();
  }

  async startQR() {

    var _this = this;

    var src = await QRCode.toDataURL(this.self.user.current_user.id.toString(), {
      errorCorrectionLevel: 'H',
      width: 100,
      height: 100,
      margin: 0
    });

    // Generamos una string pero que siempre comience por una letra
    var fname = 'a' + Math.random().toString(36).substring(7);

    window[fname] = function () {
      var a = document.createElement('img');
      a.src = src;
      a.style.position = 'absolute';
      a.style.width = '5%';
      a.style.height = '10%';

      switch (Math.floor(Math.random() * 3)) {
        case 0:
          a.style.right = '10%';
          a.style.top = (10 + Math.floor(Math.random() * 75)) + '%';
          break;
        case 1:
          a.style.bottom = '10%';
          a.style.left = (10 + Math.floor(Math.random() * 70)) + '%';
          break;
        case 2:
          a.style.left = '10%';
          a.style.top = (10 + Math.floor(Math.random() * 75)) + '%';
          break;
      }

      a.style.zIndex = 99999;
      a.style.opacity = 0.5;
      _this.playerContainer.appendChild(a);
      setTimeout(() => {
        a.remove();
      }, 100);

      setTimeout(() => {
        window[fname]()
      }, Math.floor(Math.random() * 300000));
    }

    setTimeout(() => {
      window[fname]();
    }, Math.floor(1000 + (Math.random() * 5000)));


  }

  /**
   * Timer que actualiza en el Storage la info de reproducción
   *
   * @memberof Player
   */
  startTimer() {
    setInterval(function () {
      this.secondsAlive++;

      // Lo guardamos en el storage
      localStorage.setItem(this.contentId + "_seconds", this.secondsAlive);

    }, 1000);
  }


  /**
   * Lanza la reproducción en Chromecast
   *
   * @memberof Player
   */
  async startChromecast() {
    this.options.chromecast = true;

    // Destruimos el player
    this.destroy();

    // Iniciamos el nuevo player
    await this.init();

    // Enviamos el evento de que se ha recargado el player
    this.emit(Events.PLAYER_RELOADED, this.contentId);

  }
}


